import React, { useEffect, useState } from "react";
import { Pencil } from "lucide-react";
import Button from "../ui/Button";
import CompanyDetailsForm from "../forms/CompanyDetailsForm";
import { useAppSelector } from "src/redux/hooks";
import { formatAddress } from "src/utils/formatAddress";

const PREFIX_LABELS: { key: string; label: string; fallback: string }[] = [
  { key: "saleOrder", label: "Sale Order", fallback: "SO" },
  { key: "purchaseOrder", label: "Purchase Order", fallback: "PO" },
  { key: "product", label: "Product", fallback: "P" },
  { key: "adjustmentOrder", label: "Stock Adjustment", fallback: "ADJ" },
  { key: "transaction", label: "Inventory Transaction", fallback: "TRN" },
  { key: "transfer", label: "Stock Transfer", fallback: "TRF" },
];

const DetailStat: React.FC<{
  label: string;
  value: React.ReactNode;
  className?: string;
}> = ({ label, value, className }) => (
  <div className={className}>
    <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">
      {label}
    </div>
    <div className="mt-0.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
      {value || "-"}
    </div>
  </div>
);

const TenantDetailsCard: React.FC = () => {
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const tenant = user?.tenantDetails;

  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";

  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (tenant) {
      setIsEditing(false);
    }
  }, [tenant]);

  if (isEditing) {
    return <CompanyDetailsForm handleCancel={() => setIsEditing(false)} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-2">
          <DetailStat label="Company Name" value={tenant?.name} />
          <DetailStat label="Company Email" value={tenant?.email} />
          <DetailStat label="Company Mobile" value={tenant?.mobile} />
          <DetailStat label="GST Number" value={tenant?.gst} />
          <DetailStat
            label="Address"
            value={formatAddress(tenant)}
            className="sm:col-span-2"
          />
        </div>

        {isTenantAdmin && (
          <Button
            onClick={() => setIsEditing(true)}
            variant="outline"
            className="shrink-0"
          >
            <Pencil className="w-4 h-4 mr-1" /> Edit
          </Button>
        )}
      </div>

      <div className="border-t border-gray-100 pt-4 dark:border-gray-700">
        <div className="mb-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            ID Prefixes
          </h4>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Used when generating new order and document IDs.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {PREFIX_LABELS.map(({ key, label, fallback }) => (
            <div
              key={key}
              className="rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2 dark:border-gray-700 dark:bg-gray-900/30"
            >
              <div className="text-xs text-gray-400 dark:text-gray-500">
                {label}
              </div>
              <div className="font-mono text-sm font-semibold text-gray-900 dark:text-gray-100">
                {(tenant?.idPrefixes as any)?.[key] || fallback}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-gray-100 pt-4 dark:border-gray-700">
        <div className="mb-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Terminology
          </h4>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            What you call a warehouse, used everywhere the app refers to one.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <DetailStat
            label="Singular"
            value={tenant?.warehouseLabel?.singular || "Warehouse"}
          />
          <DetailStat
            label="Plural"
            value={tenant?.warehouseLabel?.plural || "Warehouses"}
          />
        </div>
      </div>
    </div>
  );
};

export default TenantDetailsCard;
